import ApiClient from '@afosto/api/ApiClient';

class Allocation {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Attach items to a claim
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  attachItemsToClaim(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}/attach`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Cancels the claim
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description cancelClaim
   * @returns {Promise}
   */
  cancelClaim(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}/cancel`,
      method: 'post',
      ...options,
    });
  }

  /**
   * Get claim status
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description returns the claim status
   * @returns {Promise}
   */
  claimIsCancable(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}/status`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Confirm the claim
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Confirm a reservation claim and make it an actual claim
   * @returns {Promise}
   */
  confirmClaim(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}/confirm`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Create a claim projection
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a Projection to generate the available allocation options
   * @returns {Promise}
   */
  createClaimProjection(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/projection/claims`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Create a claim
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  createClaims(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Extend the claim
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Extend the time the inventory is claimed
   * @returns {Promise}
   */
  extendClaim(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}/extend`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Get a claim
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Get a claim and it&#39;s status
   * @returns {Promise}
   */
  getClaim(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a claim
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Get a claim and it&#39;s status
   * @returns {Promise}
   */
  getClaimItems(id, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}/items`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a list of claims
   *
   * @param {Object} options - The additional request options
   * @description Get a claim and it&#39;s status
   * @returns {Promise}
   */
  getClaims(options = {}) {
    return this.apiClient.request({
      url: `/wms/claims`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Remove attached items
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create an optimized set of claims
   * @returns {Promise}
   */
  removeClaimItem(id, body, options = {}) {
    return this.apiClient.request({
      url: `/wms/claims/${id}`,
      method: 'delete',
      data: body,
      ...options,
    });
  }

  /**
   * Search claims
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Returns a list for the search query
   * @returns {Promise}
   */
  searchClaims(body, options = {}) {
    return this.apiClient.request({
      url: `/wms/search/claims`,
      method: 'post',
      data: body,
      ...options,
    });
  }
}

export const allocation = new Allocation();

export default Allocation;
